import { isAfter, isBefore, isFirstDayOfMonth, isLastDayOfMonth } from 'date-fns'
import { ChangeEventHandler } from 'react'
import { styled } from 'styled-components'
import {
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getLocaleDateString,
  getMonthFromDateString,
  getMonthList,
  mqMin,
  space,
  tok,
  tokPx,
} from '../../../utils'
import { Typography } from '../Typography'

export interface MonthPickerProps {
  name: string
  className?: string
  availableDates: string[]
  locale: string
  maxDateString?: string
  minDateString?: string
  selectedMonth?: string | [string, string]
  setSelectedDates: (dates: string | [string, string]) => void
}

const StyledWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${space('primitives_space_8')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    grid-template-columns: repeat(auto-fit, minmax(7rem, 1fr));
  }
`

const ContentWrapper = styled.div`
  position: relative;
`

const StyledLabel = styled.label`
  color: ${tok('semantics_content_color')};
  align-items: center;
  background-color: ${tok('semantics_selectors_background_color')};
  border: 2px solid ${tok('semantics_selectors_border_color')};
  border-radius: ${(tokPx('primitives_corner_radius_l'))};
  display: flex;
  flex: 1 0 0;
  height: 8rem;
  justify-content: center;
  padding: ${space('primitives_space_24')} ${space('primitives_space_8')};
  text-align: center;

  & > div {
    display: flex;
    flex-direction: column;
  }

  &:hover {
    border-color: ${tok('semantics_selectors_hover_border_color')};
    background-color: ${tok('semantics_selectors_hover_background_color')};
  }
`

const StyledYear = styled(Typography)`
  color: ${tok('semantics_content_color')};
`

const HiddenRadio = styled.input`
  left: 0;
  visibility: hidden;
  position: absolute;
  top: 0;

  &:checked + ${StyledLabel} {
    color: ${tok('semantics_selectors_selected_content_color')};
    background-color: ${tok('semantics_selectors_selected_background_color')};

    ${StyledYear} {
      color: ${tok('semantics_selectors_selected_content_color')};
    }

    &:hover {
      color: ${tok('semantics_selectors_selected_hover_content_color')};
      background: ${tok('semantics_selectors_selected_hover_background_color')};

      ${StyledYear} {
        color: ${tok('semantics_selectors_selected_hover_content_color')};
      }
    }
  }

  &:disabled + ${StyledLabel} {
    opacity: ${tok('primitives_opacity_disabled')}%;
  }

  &:not(:disabled) + ${StyledLabel} {
    cursor: pointer;
  }
`

export function MonthPicker({
  name,
  className,
  availableDates,
  locale,
  maxDateString,
  minDateString,
  selectedMonth,
  setSelectedDates,
}: MonthPickerProps) {
  const monthList = getMonthList(locale)

  const onChange: ChangeEventHandler = (e) => {
    const { value } = e.target as HTMLInputElement
    const lastDayOfMonth = getLastDayOfMonth(value)
    const lastDayOfMonthString = getLocaleDateString(getLastDayOfMonth(value))

    const isStartDateBeforeMinDate = minDateString && isBefore(new Date(value), new Date(minDateString))
    const isEndDateAfterMaxDate = maxDateString && isAfter(lastDayOfMonth, new Date(maxDateString))

    const startDate = isStartDateBeforeMinDate ? minDateString : value
    const endDate = isEndDateAfterMaxDate ? maxDateString : lastDayOfMonthString

    const isStartDateAfterMinDate = minDateString && isAfter(new Date(startDate), new Date(minDateString))
    const isEndDateBeforeMaxDate = maxDateString && isBefore(new Date(endDate), new Date(maxDateString))

    const startDateToSet = isStartDateAfterMinDate ? getLocaleDateString(getFirstDayOfMonth(endDate)) : startDate
    const endDateToSet = isEndDateBeforeMaxDate ? getLocaleDateString(getLastDayOfMonth(startDate)) : endDate

    const areStartDateAndEndDateEqual = startDateToSet === endDateToSet

    setSelectedDates(areStartDateAndEndDateEqual ? startDateToSet : [startDateToSet, endDateToSet])
  }

  function areFirstAndLastDaySelected(dates: string | [string, string] | undefined, month: string, year: string): boolean {
    if (!dates || !Array.isArray(dates))
      return false
    const [firstDay, lastDay] = dates

    const firstDate = new Date(firstDay)
    const firstDateMonth = getMonthFromDateString(firstDay, locale)
    const firstDateYear = firstDate.getFullYear()
    const lastDate = new Date(lastDay)
    const lastDateMonth = getMonthFromDateString(lastDay, locale)
    const lastDateYear = lastDate.getFullYear()
    const yearOfTheMonth = Number.parseInt(year)

    return (
      (isFirstDayOfMonth(firstDate) || minDateString === firstDay) // firstDay is the first day of a month
      && (isLastDayOfMonth(lastDate) || maxDateString === lastDay) // lastDay is the last day of a month
      && firstDateMonth === lastDateMonth && firstDateYear === lastDateYear // the firstDay and lastDay are in the same month and year
      && firstDateMonth === month // the month of the firstDay matches the month being checked
      && firstDateYear === yearOfTheMonth // the year of the firstDay matches the year being checked
    )
  }

  function isMonthDisabled(firstDay: Date, lastDay: Date): boolean {
    return !availableDates.some((date) => {
      const parsedDate = new Date(date)
      return parsedDate >= firstDay && parsedDate <= lastDay
    })
  }

  return (
    <StyledWrapper className={className}>
      {monthList.map(({ date, firstDay, lastDay, month, year }, index) => {
        return (
          <ContentWrapper key={`${name}-${index}`}>
            <HiddenRadio
              type="radio"
              name={name}
              id={`${name}-${index}`}
              value={date}
              onChange={onChange}
              defaultChecked={areFirstAndLastDaySelected(selectedMonth, month, year)}
              disabled={isMonthDisabled(firstDay, lastDay)}
            />
            <StyledLabel htmlFor={`${name}-${index}`}>
              <div>
                <Typography variant="body-m-bold-loose">{month.charAt(0).toUpperCase() + month.slice(1)}</Typography>
                <StyledYear>{year}</StyledYear>
              </div>
            </StyledLabel>
          </ContentWrapper>
        )
      })}
    </StyledWrapper>
  )
}
